<template>
  <div>
    <v-card-title class="card-title">
      <h4>
        <span>{{ $t('assets.detail') }}</span>
      </h4>
      <v-btn text icon
             @click="$emit('deselect-asset')">
        <v-icon>close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="py-0">
      <div class="mb-1">
        {{ selectedElement.asset.name }}
      </div>
      <div class="mb-1 asset-route">
        {{ selectedElementAssetRoute }}
      </div>
      <div>
        <v-chip v-for="item in assetAttributes" :key="item.id"
                small color="primary" outlined>
            <span class="ret-chip-label">
              {{ item.assetAttribute.name }}
            </span>
          {{ item.value || '---' }}
        </v-chip>
      </div>
      <div>
        <v-chip v-for="(value, name) in displayedAssetData" :key="name"
                small color="accent" outlined>
            <span class="ret-chip-label">
              {{ $t(`variable.${name}`) }}
            </span>
          {{ valueCodeFromConfig(selectedElement.asset.deviceType, name, value) }}
        </v-chip>
      </div>

      <div v-if="commandToken && visualMode === 'command' && assetCommands.length">
        {{ $t('commands') }}<br />
        <div v-for="command in assetCommands" :key="command.label">
          <div v-if="command.params && !command.defaultParams">
            <strong>{{ $t(`assetCommands.${command.label}`) }}</strong>
            <v-text-field
              v-if="command.params"
              v-model="command.paramsValue"
              append-icon="send"
              @click:append="command.safety ? safetyDialog(command) : confirmCommand(command)"
              >
            </v-text-field>
          </div>

          <div v-if="!command.params || command.defaultParams">
            <v-btn small v-if="command.display !== false"
              @click="command.safety ? safetyDialog(command) : confirmCommand(command)"
                         :disabled="!isSelectedElementConnected"
                         :color="solveColor(command)"
            >
              {{ $t(`assetCommands.${command.label}`) }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions>
      <!--link to logs-->
      <v-btn text icon
             :disabled="!(selectedElement.asset && selectedElement.asset.shvPath)"
             @click="$router.push({name: 'logs', params: {assetId: selectedElement.assetId}})">
        <v-icon>timeline</v-icon>
      </v-btn>
      <!--link to asset-->
      <v-btn text icon
             @click="$router.push({name: 'assets/id', params: {id: selectedElement.assetId}})">
        <v-icon>devices</v-icon>
      </v-btn>
    </v-card-actions>
    <v-dialog v-if="confirmCommandDialog"
              v-model="confirmCommandDialog"
              max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ $t('confirm-command') }}
            <strong>{{$t(`assetCommands.${command.label}`)}}</strong>
          </span>
        </v-card-title>
        <v-card-text>
          <v-icon class="ret-group-prepend-icon"
                  :color="isSelectedElementConnected ? 'primary' : null">
            cast_connected
          </v-icon>
          <strong>{{selectedElementAssetRoute}}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary"
                 @click.native="cancelConfirmCommandDialog">
            <v-icon>cancel</v-icon>
            {{$t('cancel') }}
          </v-btn>
          <v-btn text color="primary"
                 @click.native="confirmConfirmCommandDialog">
            <v-icon>check_circle</v-icon>
            {{$t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="displaySafetyDialog"
              v-model="displaySafetyDialog"
              max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ $t('send-safety-request') }}:
            <strong>{{$t(`assetCommands.${command.label}`)}}</strong>
          </span>
        </v-card-title>
        <v-card-text>
          <v-btn color="primary"
                 @click.native="getSafetyToken">
            <v-icon>check_circle</v-icon>
            {{$t('get-safety-token') }}
          </v-btn>
          <br />
          <h3>{{ $t('safety-token') }}</h3> <strong><span class="preventcopy safety-token">{{ receivedSafetyToken }}</span></strong>
          <v-text-field type="text" v-model="safetyToken" :label="$t('safety-token')" ref="textbox"></v-text-field>
          <v-text-field type="password" v-model="userPassword" :label="$t('password')" ref="textbox"></v-text-field>
        </v-card-text>
        <v-card-text>
          <v-icon class="ret-group-prepend-icon"
                  :color="isSelectedElementConnected ? 'primary' : null">
            cast_connected
          </v-icon>
          <strong>{{selectedElementAssetRoute}}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary"
                 @click.native="cancelSafetyDialog">
            <v-icon>cancel</v-icon>
            {{$t('cancel') }}
          </v-btn>
          <v-btn text color="primary"
                 @click.native="sendSafetyCommandRequest">
            <v-icon>check_circle</v-icon>
            {{$t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
  .card-title {
    justify-content: space-between;
  }

  .asset-route {
    font-size: smaller;
  }

  .safety-token {
    font-size: 3em;
    color: black;
  }

  .preventcopy {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
</style>

<script>
import { enabledVariables } from '../visuals/components/shapes/'
import { filterKeys } from '../helpers/utilities.js'

export default {
  data () {
    return {
      displayedAssetData: {},
      commandToken: this.$cookies.get('command'),
      command: null,
      confirmCommandDialog: false,
      displaySafetyDialog: false,
      safetyToken: '',
      userPassword: '',
      receivedSafetyToken: ''
    }
  },

  props: {
    selectedElement: {
      type: Object,
      required: true
    },
    selectedElementAssetRoute: {
      type: String,
      required: true
    },
    visualMode: {
      type: String
    }
  },

  mounted () {
    const assetData = this.$store.state.assetData[this.selectedElement.assetId]
    if (assetData) {
      const filteredVariables = enabledVariables()[this.selectedElement.asset.deviceType] || []
      this.displayedAssetData = filterKeys(filteredVariables, assetData)
    }
  },

  methods: {
    confirmCommand (command) {
      this.command = command
      this.confirmCommandDialog = true
    },
    safetyDialog (command) {
      this.command = command
      this.displaySafetyDialog = true
    },
    solveColor (command) {
      const variableName = command.correspondingVariable ? command.correspondingVariable : 'status'
      return this.selectedAssetData[variableName] === command.correspondingValue ? 'primary' : ''
    },
    cancelConfirmCommandDialog () {
      this.command = null
      this.confirmCommandDialog = false
    },
    cancelSafetyDialog () {
      this.command = null
      this.userPassword = null
      this.safetyToken = null
      this.receivedSafetyToken = null
      this.displaySafetyDialog = false
    },
    confirmConfirmCommandDialog () {
      this.callAssetMethod()
      this.cancelConfirmCommandDialog()
    },
    getSafetyToken () {
      const { selectedElement, command, commandToken } = this
      const { assetId } = selectedElement

      this.$http.post(`/send-command`, { assetId, method: command.method, commandToken, requestType: 'sync' })
        .then((response) => {
          // OK
          this.receivedSafetyToken = response.data.content
        }, (error) => {
          // error callback
          // commit('handleAxiosResponse', { modelName: '', response: error.response })
          console.log(error.response)
        })
    },
    sendSafetyCommandRequest () {
      const { selectedElement, commandToken, userPassword, safetyToken } = this
      const { assetId } = selectedElement
      this.$store.dispatch('sendSafetyCommandRequest', { assetId, commandToken, userPassword, safetyToken })
      this.cancelSafetyDialog()
    },
    callAssetMethod () {
      const { selectedElement, command, commandToken } = this
      const { assetId } = selectedElement
      const params = command.paramsValue ? command.paramsValue : command.defaultParams
      this.$store.dispatch('callAssetMethod', {
        assetId,
        method: command.method,
        commandToken,
        attribute: command.attribute,
        params: params
      })
    },
    deviceConfig (deviceType) {
      return this.deviceTypes.find(type => type.name === deviceType)
    },
    valueCodeFromConfig (deviceType, variableName, value) {
      const deviceDefinition = this.deviceConfig(deviceType).variables[variableName]
      if (deviceDefinition.type === 'analog') {
        return value
      } else {
        const config = deviceDefinition.config
        return config[value] ? this.$t(`variable-label.${config[value].code}`) : 'N/A'
      }
    }

  },

  computed: {
    assetCommands () {
      const { availableCommands, enabledCommands } = this.selectedElement.asset
      if (!enabledCommands || !enabledCommands.length) {
        return availableCommands
      }
      return availableCommands.filter((command) => enabledCommands.includes(command.method))
    },
    assetAttributes () {
      const { asset } = this.selectedElement
      return asset && asset.assetAttributesInAssets
    },
    assetData () {
      return this.$store.state.assetData
    },
    deviceTypes () {
      return this.$store.state.deviceTypes
    },
    selectedAssetData () {
      return this.assetData[this.selectedElement.assetId] || {}
    },
    isSelectedElementConnected () {
      return !!this.selectedAssetData
    }
  },

  watch: {
    selectedAssetData: {
      handler (data) {
        if (data) {
          const filteredVariables = enabledVariables()[this.selectedElement.asset.deviceType] || []
          this.displayedAssetData = filterKeys(filteredVariables, data)
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>
